// client/src/features/auth/Login.jsx

import React, {
  useRef,
  useState,
  useEffect,
} from 'react';

import {
  useNavigate,
  Link,
} from 'react-router-dom';

import { useDispatch } from 'react-redux';

import {
  setCredentials,
} from './authSlice';

import {
  useLoginMutation,
  useMicrosoftLoginMutation,
} from './authApiSlice';

import usePersist from '../../hooks/usePersist';
import useTitle from '../../hooks/useTitle';

import PulseLoader from 'react-spinners/PulseLoader';

import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  Input,
  Paper,
  Typography,
  useTheme,
} from '@mui/material';

import { tokens } from '../../theme';
import { fivebyfive } from '../../assets';
import { motion } from 'framer-motion';

import { useMsal } from '@azure/msal-react';


/*
 * Small inline Microsoft logo.
 *
 * No external image dependency and gives the button the same
 * recognizable provider branding the Google button had.
 */
const MicrosoftIcon = () => (
  <Box
    component="svg"
    viewBox="0 0 23 23"
    sx={{
      width: 20,
      height: 20,
      flexShrink: 0,
    }}
  >
    <rect
      x="1"
      y="1"
      width="10"
      height="10"
      fill="#f35325"
    />

    <rect
      x="12"
      y="1"
      width="10"
      height="10"
      fill="#81bc06"
    />

    <rect
      x="1"
      y="12"
      width="10"
      height="10"
      fill="#05a6f0"
    />

    <rect
      x="12"
      y="12"
      width="10"
      height="10"
      fill="#ffba08"
    />
  </Box>
);


const Login = () => {
  useTitle('Employee Login');

  const theme = useTheme();
  const colors = tokens(
    theme.palette.mode
  );

  const userRef = useRef(null);
  const errRef = useRef(null);

  const [username, setUsername] =
    useState('');

  const [password, setPassword] =
    useState('');

  const [errMsg, setErrMsg] =
    useState('');

  /*
   * This should be backed by localStorage via usePersist.
   *
   * PersistLogin reads the same value after a browser refresh.
   */
  const [
    persist,
    setPersist,
  ] = usePersist();

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [
    login,
    {
      isLoading: pwLoading,
    },
  ] = useLoginMutation();

  const [
    microsoftLogin,
    {
      isLoading: mLoading,
    },
  ] = useMicrosoftLoginMutation();

  const { instance } = useMsal();

  /*
   * Legacy username/password login can remain in the source
   * but is currently disabled.
   */
  const allowUserPassLogin = false;


  useEffect(() => {
    if (
      allowUserPassLogin &&
      userRef.current
    ) {
      userRef.current.focus();
    }
  }, [allowUserPassLogin]);


  useEffect(() => {
    setErrMsg('');
  }, [
    username,
    password,
  ]);


  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const {
        accessToken,
        isFirstLogin,
      } = await login({
        username,
        password,
      }).unwrap();

      dispatch(
        setCredentials({
          accessToken,
        })
      );

      setUsername('');
      setPassword('');

      navigate(
        isFirstLogin
          ? '/reset-password'
          : '/console'
      );
    } catch (err) {
      if (!err.status) {
        setErrMsg(
          'No Server Response'
        );
      } else if (
        err.status === 400
      ) {
        setErrMsg(
          'Missing Username or Password'
        );
      } else if (
        err.status === 401
      ) {
        setErrMsg(
          'Unauthorized'
        );
      } else {
        setErrMsg(
          err.data?.message ||
          'Login failed'
        );
      }

      if (
        allowUserPassLogin &&
        errRef.current
      ) {
        errRef.current.focus();
      }
    }
  };


  const handleUserInput = (e) =>
    setUsername(
      e.target.value
    );


  const handlePwdInput = (e) =>
    setPassword(
      e.target.value
    );


  const handleToggle = () => {
    setPersist(
      (prev) => !prev
    );
  };


  const handleMicrosoftLogin =
    async () => {
      setErrMsg('');

      try {
        const response =
          await instance.loginPopup({
            scopes: [
              'openid',
              'profile',
              'email',
            ],

            prompt:
              'select_account',
          });

        const id_token =
          response?.idToken;

        if (!id_token) {
          setErrMsg(
            'Microsoft sign-in failed: missing token'
          );

          return;
        }

        /*
         * Authenticate the Microsoft identity against our API.
         *
         * The backend should return an access token AND set its
         * refresh-token HTTP-only cookie.
         */
        const {
          accessToken,
          isFirstLogin,
        } =
          await microsoftLogin({
            id_token,
          }).unwrap();

        dispatch(
          setCredentials({
            accessToken,
          })
        );

        /*
         * DO NOT force persist=true here.
         *
         * The checkbox controls whether this device should
         * automatically restore the session after a refresh.
         *
         * usePersist should already have saved the checkbox
         * state to localStorage.
         */
        navigate(
          isFirstLogin
            ? '/reset-password'
            : '/console'
        );
      } catch (err) {
        console.error(
          'Microsoft login failed:',
          err
        );

        setErrMsg(
          'Microsoft sign-in failed or was cancelled'
        );
      }
    };


  if (
    pwLoading ||
    mLoading
  ) {
    return (
      <PulseLoader
        color="#000"
      />
    );
  }


  return (
    <Box
      sx={{
        display: 'flex',
        width: '100%',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        backgroundColor:
          colors.grey[600],
      }}
    >
      <motion.div
        initial={{
          opacity: 0,
          scale: 0.8,
        }}
        animate={{
          opacity: 1,
          scale: 1,
        }}
        exit={{
          opacity: 0,
          scale: 0.8,
        }}
        transition={{
          duration: 0.6,
        }}
      >
        <Paper
          elevation={6}
          sx={{
            p: 6,
            width: '100%',
            maxWidth: '400px',
            textAlign: 'center',
            boxShadow:
              '0px 0px 10px rgba(0,0,0,1)',
            backgroundColor:
              colors.grey[200],
            borderRadius: '20px',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent:
                'center',
              mb: 2,
            }}
          >
            <img
              src={fivebyfive}
              alt="5x5"
              style={{
                width: '100px',
              }}
            />
          </Box>

          <Typography
            variant="h2"
            color={
              colors.blueAccent[700]
            }
            gutterBottom
          >
            Login
          </Typography>


          {/* Microsoft Sign-In */}
          <Box
            sx={{
              display: 'flex',
              justifyContent:
                'center',
              mt: 3,
              mb: 2,
            }}
          >
            <Button
              onClick={
                handleMicrosoftLogin
              }
              fullWidth
              variant="contained"
              startIcon={
                <MicrosoftIcon />
              }
              sx={{
                fontFamily:
                  'Raleway, sans-serif',

                fontSize:
                  '16px',

                textTransform:
                  'none',

                py: 1.3,

                backgroundColor:
                  colors.primary[500],

                '&:hover': {
                  backgroundColor:
                    colors.primary[700],
                },
              }}
            >
              Sign in with Microsoft
            </Button>
          </Box>


          {/*
           * IMPORTANT:
           *
           * This is OUTSIDE allowUserPassLogin so Microsoft
           * SSO users can actually control persistence.
           */}
          <Box
            sx={{
              display: 'flex',
              justifyContent:
                'center',
              mb: 3,
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={persist}
                  onChange={
                    handleToggle
                  }
                  color="primary"
                />
              }
              label="Trust This Device"
            />
          </Box>


          <Typography
            variant="body2"
            sx={{
              mb: 3,
              mt: -2,
              color:
                colors.grey[500],
            }}
          >
            Keep me signed in after
            refreshing or reopening
            DropSwitch.
          </Typography>


          {/* Error */}
          {errMsg && (
            <Typography
              variant="body2"
              color="error"
              ref={errRef}
              sx={{
                mb: 2,
              }}
              tabIndex={-1}
            >
              {errMsg}
            </Typography>
          )}


          {/*
           * Legacy username/password login.
           *
           * Currently disabled but preserved if you ever need it.
           */}
          {allowUserPassLogin && (
            <Box
              component="form"
              onSubmit={
                handleSubmit
              }
              id="login-form"
            >
              <FormControl
                fullWidth
                sx={{
                  mb: 2,
                }}
              >
                <InputLabel
                  htmlFor="username"
                  sx={{
                    fontSize:
                      '18px',

                    color:
                      colors.primary[600],
                  }}
                >
                  Username
                </InputLabel>

                <Input
                  id="username"
                  type="text"
                  value={username}
                  onChange={
                    handleUserInput
                  }
                  autoComplete="off"
                  required
                  inputRef={
                    userRef
                  }
                />
              </FormControl>

              <FormControl
                fullWidth
                sx={{
                  mb: 3,
                }}
              >
                <InputLabel
                  htmlFor="password"
                  sx={{
                    fontSize:
                      '18px',

                    color:
                      colors.primary[600],
                  }}
                >
                  Password
                </InputLabel>

                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={
                    handlePwdInput
                  }
                  required
                />
              </FormControl>

              <Button
                type="submit"
                fullWidth
                variant="contained"
                color="primary"
                sx={{
                  mt: 2,

                  fontFamily:
                    'Raleway, sans-serif',

                  fontSize:
                    '16px',

                  backgroundColor:
                    colors.primary[500],

                  '&:hover': {
                    backgroundColor:
                      colors.primary[700],
                  },
                }}
              >
                Sign In
              </Button>
            </Box>
          )}


          <Box
            sx={{
              mt: 3,
            }}
          >
            <Link
              to="/"
              style={{
                fontFamily:
                  'Raleway, sans-serif',

                textDecoration:
                  'none',

                color:
                  colors.blueAccent[700],
              }}
            >
              Back to Home
            </Link>
          </Box>
        </Paper>
      </motion.div>
    </Box>
  );
};


export default Login;